import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Box, Typography } from '@mui/material';
import { RootState } from '../../store/store';
import {
  addControlPoint,
  moveControlPoint,
  removeControlPoint,
} from '../../store/slices/missionPlannerSlice';
import {
  CAPABILITY,
  ProfileAxis,
  inRange,
  curveSeries,
} from '../../utils/profile';

interface ProfileChartProps {
  axis: ProfileAxis;
  xMaxKm: number;
  defaultValue: number;
}

// Fixed geometry keeps the recharts line and the interaction overlay aligned,
// and deterministic.
//
// The plot rectangle is inset by the margin *plus* the axis extents — they are
// additive, not alternatives. Mirroring only the margin put the overlay's
// origin at x=40 while recharts drew from x=80, so the curve missed every
// control point by ~15% of the chart width. The two agreed only at the
// top-right, the one corner with no axis on either side of it (#447).
//
// Both axis extents are pinned here rather than left to recharts' defaults, so
// the two sides cannot drift apart again: the overlay derives its rectangle
// from the same numbers the chart is given.
const W = 320;
const H = 180;
const M = { top: 10, right: 12, bottom: 24, left: 40 };
const AXIS = { yWidth: 40, xHeight: 30 };
const X0 = M.left + AXIS.yWidth;
const X1 = W - M.right;
const Y0 = M.top;
const Y1 = H - M.bottom - AXIS.xHeight;

/**
 * The plot rectangle both the recharts line and the overlay are drawn in.
 *
 * Exported so tests place clicks against the real geometry instead of keeping
 * their own copy of it. Two test files had reimplemented these numbers, so a
 * corrected rectangle broke them — the mapping was what changed, and a test
 * that hardcodes the mapping cannot tell a fix from a regression (#447).
 */
export const PLOT_RECT = { x0: X0, x1: X1, y0: Y0, y1: Y1 } as const;

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

/**
 * Editable altitude/speed profile chart (issue #298). The recharts line shows
 * the interpolated curve; a transparent SVG overlay renders the control points
 * and handles editing: click empty space to add, drag a point to adjust,
 * double-click to remove. Out-of-range values are clamped (see the slice).
 */
export const ProfileChart: React.FC<ProfileChartProps> = ({
  axis,
  xMaxKm,
  defaultValue,
}) => {
  const dispatch = useDispatch();
  const curve = useSelector((state: RootState) =>
    axis === 'altitude'
      ? state.missionPlanner.draft.profile.altitudeCurve
      : state.missionPlanner.draft.profile.speedCurve
  );
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const cap = CAPABILITY[axis];
  const xMax = xMaxKm > 0 ? xMaxKm : 1;

  // Five clean round Y ticks across the envelope (0/30/60/90/120, 0/5/10/15/20).
  const yTicks = Array.from(
    { length: 5 },
    (_, i) => cap.min + (i * (cap.max - cap.min)) / 4
  );
  // Distances are km; short routes span tens of metres, so a fixed 1-decimal
  // format collapses every tick to "0.0". Scale decimals to the span instead.
  const xDecimals = xMax >= 10 ? 0 : xMax >= 1 ? 1 : xMax >= 0.1 ? 2 : 3;

  const toX = (d: number) => X0 + (clamp(d, 0, xMax) / xMax) * (X1 - X0);
  const toY = (v: number) =>
    Y1 - ((clamp(v, cap.min, cap.max) - cap.min) / (cap.max - cap.min)) * (Y1 - Y0);
  const invX = (px: number) => ((px - X0) / (X1 - X0)) * xMax;
  const invY = (py: number) =>
    cap.min + ((Y1 - py) / (Y1 - Y0)) * (cap.max - cap.min);

  const localPoint = (clientX: number, clientY: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    return { px: clientX - (rect?.left ?? 0), py: clientY - (rect?.top ?? 0) };
  };

  // Drag: track the pointer at the window level so it keeps up past the dot.
  useEffect(() => {
    if (dragIndex === null) return;
    const onMove = (e: PointerEvent) => {
      const { px, py } = localPoint(e.clientX, e.clientY);
      dispatch(
        moveControlPoint({
          axis,
          index: dragIndex,
          distanceKm: clamp(invX(px), 0, xMax),
          value: invY(py),
        })
      );
    };
    const onUp = () => setDragIndex(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragIndex, axis, xMax]);

  const series = curveSeries(curve, xMax, defaultValue);

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {cap.label} vs distance ({cap.unit})
      </Typography>
      <Box sx={{ position: 'relative', width: W, height: H }}>
        <LineChart
          width={W}
          height={H}
          margin={M}
          data={series}
          aria-hidden="true"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="distanceKm"
            domain={[0, xMax]}
            tickFormatter={(d: number) => d.toFixed(xDecimals)}
            fontSize={10}
            height={AXIS.xHeight}
          />
          <YAxis
            domain={[cap.min, cap.max]}
            ticks={yTicks}
            tickFormatter={(v: number) => String(Math.round(v))}
            fontSize={10}
            width={AXIS.yWidth}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#1976d2"
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>

        {/* Interaction overlay — control points + click-to-add. */}
        <svg
          ref={svgRef}
          width={W}
          height={H}
          role="application"
          aria-label={`${cap.label} profile editor`}
          style={{ position: 'absolute', inset: 0, touchAction: 'none' }}
        >
          {/* Background capture: click empty space to add a control point. */}
          <rect
            x={X0}
            y={Y0}
            width={X1 - X0}
            height={Y1 - Y0}
            fill="transparent"
            data-testid={`profile-bg-${axis}`}
            onClick={(e) => {
              const { px, py } = localPoint(e.clientX, e.clientY);
              dispatch(
                addControlPoint({
                  axis,
                  distanceKm: clamp(invX(px), 0, xMax),
                  value: invY(py),
                })
              );
            }}
          />
          {curve.points.map((p, i) => {
            const bad = !inRange(axis, p.value);
            return (
              <circle
                key={i}
                cx={toX(p.distanceKm)}
                cy={toY(p.value)}
                r={4}
                // Muted CVD-safe pair (issue #341): blue point, vermillion
                // (not pure red) for out-of-range — legible under deuteranopia.
                fill={bad ? '#d55e00' : '#0072b2'}
                stroke="#fff"
                strokeWidth={1}
                style={{ cursor: 'grab' }}
                data-testid={`profile-point-${axis}-${i}`}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setDragIndex(i);
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  dispatch(removeControlPoint({ axis, index: i }));
                }}
              />
            );
          })}
        </svg>
      </Box>
    </Box>
  );
};
